import React from 'react'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import FormGroup from '@mui/material/FormGroup'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'

import PhotoCamera from '@mui/icons-material/PhotoCamera'

import { SearchBoxView } from 'src/components/searchbox/views'
import { HeaderBarScope } from '../tc_scopes'
import { ToolbarDivider, ToolbarSectionTitleResponsive, ToolbarToggleRow } from 'src/utils/views'

export { HeaderBarScope as ToolbarScope }

const LOG = Logger.get('ToolbarView')

const TopIconButton = styled(IconButton)({
    width: 38,
    height: 38,
    borderRadius: 9,
    color: '#fff',
    flex: '0 0 auto',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, .08)' },
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        flexGrow: 1,
        gap: 12,
        marginLeft: 5,
        minWidth: 0,
        width: 0
    },
    searchBar: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexGrow: 1,
        minWidth: 220,
        width: 0,
        '@media (max-width: 720px)': { display: 'none' }
    },
    searchBox: {
        flexGrow: 1,
        minWidth: 0,
        '& > div:first-of-type': {
            minHeight: 43,
            padding: '0 5px 0 10px',
            borderRadius: 9,
            border: '1px solid rgba(255, 255, 255, .18)',
            backgroundColor: 'rgba(255, 255, 255, .06)',
            transition: 'border-color .15s ease, box-shadow .15s ease',
            '&:focus-within': {
                borderColor: 'var(--green-700)',
                boxShadow: '0 0 0 3px rgba(38, 229, 31, .12)'
            }
        },
        '& > div:first-of-type > div': {
            minWidth: 0,
            minHeight: 41,
            gap: 4,
            '&.focused': { boxShadow: 'none', borderColor: 'transparent' }
        },
        '& input': {
            backgroundColor: 'transparent',
            color: '#f4f4f4',
            fontSize: 14,
            '&::placeholder': { color: 'rgba(255, 255, 255, .52)', opacity: 1 }
        },
        '& .MuiInputBase-root': { color: '#f4f4f4', fontSize: 13 },
        '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, .62)' },
        '& [data-tag-index]': {
            height: 25,
            borderRadius: 999,
            border: '1px solid rgba(255, 255, 255, .2)',
            backgroundColor: 'rgba(255, 255, 255, .1)',
            color: '#f4f4f4',
            fontSize: 11.5,
            fontWeight: 700,
            '& svg': { color: 'rgba(255, 255, 255, .68)' },
            '& svg:hover': { color: '#fff' }
        },
        '& > div:not(:first-of-type)': {
            padding: 7,
            borderRadius: 10,
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-2)',
            color: 'var(--ink)',
            fontSize: 14
        }
    },
    adornment: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flex: '0 0 auto'
    },
    toggles: {
        display: 'flex',
        flexDirection: 'column'
    }
})

type ToolbarViewProps = {
    className?: string
    scope: HeaderBarScope
}

export const ToolbarView = function ({ className, ...props }: ToolbarViewProps) {
    const ctx = ToolbarContext.use(props)
    const { classes, scope } = ctx

    return (
        <div className={clsx(classes.view, className)}>
            <ToolbarDivider orientation="vertical" variant="middle" flexItem />
            <ToolbarSectionTitleResponsive>Relações Societárias</ToolbarSectionTitleResponsive>
            <div className={classes.searchBar}>
                <SearchBoxView
                    className={classes.searchBox}
                    scope={scope.search}
                    endAdornment={
                        <div className={classes.adornment}>
                            <FormGroup className={classes.toggles}>
                                <Tooltip title="Substitui o recorte atual a cada nó escolhido, em vez de somar ao que já está no grafo.">
                                    <ToolbarToggleRow
                                        label="Filtro exclusivo"
                                        control={
                                            <Switch
                                                size="small"
                                                checked={scope.exclusive}
                                                onChange={ctx.handleChangeExclusiveCheck}
                                            />
                                        }
                                    />
                                </Tooltip>
                                <Tooltip title="Deixa de fora as empresas com situação cadastral inativa.">
                                    <ToolbarToggleRow
                                        label="Mostrar ativos"
                                        control={
                                            <Switch
                                                size="small"
                                                checked={scope.onlyActives}
                                                onChange={ctx.handleChangeAtivoCheck}
                                            />
                                        }
                                    />
                                </Tooltip>
                            </FormGroup>
                            {scope.acceptPhotos && (
                                <Tooltip title="Buscar por foto">
                                    <TopIconButton
                                        aria-label="Buscar por foto"
                                        onClick={ctx.handleonOpenSearchByPicture}
                                    >
                                        <PhotoCamera />
                                    </TopIconButton>
                                </Tooltip>
                            )}
                        </div>
                    }
                />
            </div>
        </div>
    )
}

class ToolbarContext {
    static #create = () => new ToolbarContext()

    static use({ scope }: ToolbarViewProps) {
        bindUpdate(React, scope)
        const context = React.useMemo(ToolbarContext.#create, [])
        context.#update(scope)
        return context
    }

    scope!: HeaderBarScope
    classes!: ReturnType<typeof useStyles>['classes']

    #update(scope: HeaderBarScope) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    handleChangeExclusiveCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.onChangeExclusive(e.target.checked).catch(LOG.caught)
    }

    handleChangeAtivoCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.onChangeOnlyActives(e.target.checked).catch(LOG.caught)
    }

    handleonOpenSearchByPicture = () => {
        this.scope.onOpenSearchByPicture()
    }
}
